/**
 * The universal command box: students, guardians, staff, courses, sections,
 * applications, forms, assets, activities, routes, saved reports — and plain
 * phrases ("graduation audit", "bus 22", "unpaid fees") that answer with the
 * door they name. Results respect the caller's modules because the API does.
 *
 * With "Remember recent searches" on (User Preferences), the last five
 * searches that led somewhere are kept on the account and offered when the
 * box is focused and empty. Off, nothing is stored or shown.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { pushRecentSearch } from '@cornerstone/education-domain'
import { request } from '@/lib/api'
import { resolvePreferences, savePreferences } from '@/lib/preferences'
import { useSession } from '@/lib/session'
import { IconSearch } from './icons'

interface Hit {
  type: string
  label: string
  sub: string
  to: string
}

const TYPE_LABELS: Record<string, string> = {
  command: 'Go to',
  student: 'Students',
  guardian: 'Guardians',
  staff: 'Staff',
  course: 'Courses',
  section: 'Sections',
  application: 'Applications',
  form: 'Forms',
  asset: 'Assets',
  activity: 'Activities',
  route: 'Routes',
  report: 'Reports',
}

export function CommandSearch() {
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<Hit[] | null>(null)
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const boxRef = useRef<HTMLDivElement>(null)
  const { preferences, setPreferences } = useSession()
  const [focused, setFocused] = useState(false)
  const recents = preferences.rememberRecentSearches ? preferences.recentSearches : []
  const showRecents = focused && q.trim() === '' && recents.length > 0

  function saveRecents(next: string[]) {
    setPreferences(resolvePreferences({ ...preferences, recentSearches: next }))
    savePreferences({ recentSearches: next })
      .then((result) => setPreferences(resolvePreferences(result.preferences)))
      .catch(() => {})
  }

  useEffect(() => {
    if (q.trim().length < 2) {
      setHits(null)
      return
    }
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      request<{ hits: Hit[] }>('/search', { query: { q: q.trim() }, signal: controller.signal })
        .then((result) => {
          setHits(result.hits)
          setOpen(true)
        })
        .catch(() => {})
    }, 250)
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [q])

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) {
        setOpen(false)
        setFocused(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const grouped = new Map<string, Hit[]>()
  for (const hit of hits ?? []) {
    const bucket = grouped.get(hit.type)
    if (bucket) bucket.push(hit)
    else grouped.set(hit.type, [hit])
  }

  return (
    <div ref={boxRef} className="topbar__search" style={{ position: 'relative' }}>
      <IconSearch className="topbar__search-icon" />
      <label className="sr-only" htmlFor="global-search">
        Search everything — people, classes, things and commands
      </label>
      <input
        id="global-search"
        name="global-search"
        type="search"
        // Not a credential field: keeps password managers from filling it.
        data-1p-ignore
        data-lpignore="true"
        placeholder="Search everything… try “bus 31” or “graduation audit”"
        value={q}
        autoComplete="off"
        onChange={(event) => setQ(event.target.value)}
        onFocus={() => {
          setFocused(true)
          if (hits) setOpen(true)
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return
          setOpen(false)
          setFocused(false)
        }}
      />
      {showRecents && (
        <div className="search-recents" role="listbox" aria-label="Recent searches">
          <p className="search-recents__head">
            <span>Recent searches</span>
            <button
              type="button"
              className="search-recents__clear"
              onMouseDown={(event) => {
                event.preventDefault()
                saveRecents([])
              }}
            >
              Clear
            </button>
          </p>
          {recents.map((term) => (
            <button
              key={term}
              type="button"
              role="option"
              aria-selected="false"
              className="search-recents__item"
              onMouseDown={(event) => {
                // Re-runs it: the query box fills and the results open.
                event.preventDefault()
                setQ(term)
              }}
            >
              <IconSearch className="search-recents__icon" />
              {term}
            </button>
          ))}
        </div>
      )}
      {open && hits && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            minWidth: '22rem',
            maxHeight: '26rem',
            overflowY: 'auto',
            background: 'var(--surface, #fff)',
            border: '1px solid var(--border, #d9d9e3)',
            borderRadius: '10px',
            boxShadow: '0 10px 30px rgba(10,10,30,0.18)',
            zIndex: 60,
            padding: '0.4rem',
          }}
        >
          {hits.length === 0 ? (
            <p className="row-sub" style={{ padding: '0.5rem 0.75rem', margin: 0 }}>
              Nothing answers to that.
            </p>
          ) : (
            [...grouped.entries()].map(([type, rows]) => (
              <div key={type}>
                <p className="row-sub" style={{ margin: '0.35rem 0.75rem 0.1rem', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.06em' }}>
                  {TYPE_LABELS[type] ?? type}
                </p>
                {rows.map((hit, index) => (
                  <button
                    key={`${hit.to}-${index}`}
                    type="button"
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      background: 'none',
                      border: 'none',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault()
                      setOpen(false)
                      setFocused(false)
                      if (preferences.rememberRecentSearches) saveRecents(pushRecentSearch(recents, q))
                      setQ('')
                      navigate(hit.to)
                    }}
                  >
                    <strong style={{ display: 'block', fontSize: '0.86rem' }}>{hit.label}</strong>
                    <span className="row-sub">{hit.sub}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
